/* TDB MIS · User Manuals tile (role-gated downloads)
 *
 * Tiny card placed at the bottom of /division/tdb. Two buttons:
 *   • 📘 Britto's Daily Operating Manual (.docx) — Britto + Aditya + Dipali
 *   • 📗 Roji's Monthly Stock Register Manual (.docx) — Roji + Aditya + Dipali
 *
 * Each click GETs /api/tdb-mis/user-manuals/{role} which streams the
 * .docx and writes a `mis_audit_log` row. The tile fetches metadata
 * once on mount (size · filename · version) and renders both buttons
 * regardless of viewer role · the server-side gate handles the 403.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BookOpen, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_BADGES = {
  britto: {
    icon: '📘',
    accent: 'border-amber-300 bg-amber-50 hover:bg-amber-100',
    accentText: 'text-amber-900',
    pill: 'bg-amber-200 text-amber-900',
    subtitle: 'Daily Sales · Daily Expenses · 37 categories · 15 min/day',
  },
  roji: {
    icon: '📗',
    accent: 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100',
    accentText: 'text-emerald-900',
    pill: 'bg-emerald-200 text-emerald-900',
    subtitle: 'Monthly Stock Register · sign-off · carry-forward',
  },
};

export default function TDBUserManuals({ className = '' }) {
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/tdb-mis/user-manuals`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!alive) return;
        setManuals(res.data?.manuals || []);
      } catch (e) {
        if (!alive) return;
        // 403 means viewer can't list — hide tile entirely
        if (e?.response?.status === 403) {
          setManuals([]);
        } else {
          setErr(e?.response?.data?.detail || e?.message || 'Failed to load');
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleDownload = async (role, filename) => {
    setDownloading(role);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/tdb-mis/user-manuals/${role}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        toast.error(`This manual is restricted to its owner + CEO + Aditya`);
      } else {
        toast.error(`Download failed${status ? ` (${status})` : ''}`);
      }
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="tdb-user-manuals-loading"
        className={`bg-white border border-slate-200 rounded-2xl p-6 ${className}`}
      >
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading user manuals…
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div
        data-testid="tdb-user-manuals-error"
        className={`bg-white border border-red-200 rounded-2xl p-6 ${className}`}
      >
        <div className="text-sm text-red-700">User manuals · {err}</div>
      </div>
    );
  }

  // Hidden when listing returns no manuals (403 viewer)
  if (!manuals.length) return null;

  return (
    <div
      data-testid="tdb-user-manuals-card"
      className={`bg-white border border-slate-200 rounded-2xl p-6 ${className}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-slate-700" />
            <h3 className="text-lg font-semibold text-slate-900">
              User Manuals
            </h3>
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium tracking-wide uppercase"
              data-testid="tdb-user-manuals-version"
            >
              v1.1
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Role-specific Word docs · screenshots · checklists · printable
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {manuals.map((m) => {
          const badge = ROLE_BADGES[m.role] || ROLE_BADGES.britto;
          const isBusy = downloading === m.role;
          return (
            <button
              key={m.role}
              type="button"
              data-testid={`tdb-user-manual-${m.role}-btn`}
              onClick={() => handleDownload(m.role, m.filename)}
              disabled={isBusy}
              className={`text-left border rounded-xl p-4 transition ${badge.accent} ${badge.accentText} disabled:opacity-60 disabled:cursor-wait`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xl leading-none">{badge.icon}</span>
                    <span className="text-sm font-semibold truncate">
                      {m.title}
                    </span>
                  </div>
                  <div className="text-xs opacity-80 mt-1 leading-snug">
                    {badge.subtitle}
                  </div>
                  <div className="text-[11px] opacity-70 mt-2 font-mono truncate">
                    {m.filename} · {m.size_kb} KB
                  </div>
                </div>
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${badge.pill}`}
                >
                  {isBusy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                  <span>{isBusy ? '...' : 'Download'}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-4 text-[11px] text-slate-500">
        Britto's manual ships only to Britto · Roji's manual ships only to Roji ·
        Aditya & Dipali get both · every download writes to{' '}
        <span className="font-mono">mis_audit_log</span>.
      </div>
    </div>
  );
}
